/**
 * Web coin packs via Paddle.js overlay checkout (SANDBOX). WEB ONLY: Apple 3.1.1 / Play billing — any native shell
 * (React Native WebView, Capacitor, an iOS/Android in-app browser of our own) never sees the COINS tab or loads
 * Paddle. Coins are credited server-side by the Paddle webhook (POST /paddle/webhook on handegg-server), idempotent
 * per transaction; the client only re-syncs the wallet after the overlay reports completion.
 */
import {
  COIN_PACKS,
  PADDLE_CLIENT_TOKEN,
  PADDLE_ENVIRONMENT,
  PADDLE_PRICE_IDS,
  WEB_COIN_PURCHASES_ENABLED,
} from '@hf/shared';
import { track } from '@hf/client-core/analytics';
import { account } from '../net/account';
import { wallet } from './wallet';

const PADDLE_JS = 'https://cdn.paddle.com/paddle/v2/paddle.js';

type PaddleGlobal = {
  Environment: { set(env: string): void };
  Initialize(o: { token: string; eventCallback?: (e: { name?: string; data?: unknown }) => void }): void;
  Checkout: { open(o: Record<string, unknown>): void };
};

/** True only in a plain web browser (never inside a native app shell). */
export const paddleWebAllowed = (): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const w = window as unknown as Record<string, any>;
  if (w.ReactNativeWebView || w.Capacitor?.isNativePlatform?.() || w.webkit?.messageHandlers?.handegg)
    return false;
  if (/HandEggApp|; wv\)/i.test(navigator.userAgent)) return false;
  return true;
};

/** Coin packs can be bought here: a plain web browser AND `WEB_COIN_PURCHASES_ENABLED` (currently off). */
export const webCoinPurchasesEnabled = (): boolean => WEB_COIN_PURCHASES_ENABLED && paddleWebAllowed();

let loading: Promise<PaddleGlobal> | null = null;
const loadPaddle = (): Promise<PaddleGlobal> => {
  if (!webCoinPurchasesEnabled()) return Promise.reject(new Error('paddle_not_allowed'));
  loading ??= new Promise<PaddleGlobal>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PADDLE_JS;
    s.async = true;
    s.onload = () => {
      const P = (window as unknown as { Paddle?: PaddleGlobal }).Paddle;
      if (!P) return reject(new Error('paddle_missing'));
      P.Environment.set(PADDLE_ENVIRONMENT);
      P.Initialize({ token: PADDLE_CLIENT_TOKEN, eventCallback: onPaddleEvent });
      resolve(P);
    };
    s.onerror = () => {
      loading = null;
      reject(new Error('paddle_load_failed'));
    };
    document.head.appendChild(s);
  });
  return loading;
};

let pendingPack: string | null = null;
function onPaddleEvent(e: { name?: string }): void {
  if (e.name === 'checkout.completed') {
    const pack = COIN_PACKS.find((p) => p.id === pendingPack);
    if (pack)
      track('purchase_completed', {
        product_id: pack.id,
        price_tier: pack.usd.toFixed(2),
        store: 'paddle',
        is_first_purchase: false,
        coins: pack.coins,
      });
    // The webhook is the source of truth; poll the server wallet a few times while it lands.
    let n = 0;
    const tick = (): void => {
      void wallet.sync();
      if (++n < 8) setTimeout(tick, 2500);
    };
    tick();
  }
}

export type CheckoutResult = 'opened' | 'signed_out' | 'unavailable';

/** Open the overlay checkout for `packId`, tagged with the signed-in user id (custom_data.user_id). */
export const openCoinCheckout = async (packId: string): Promise<CheckoutResult> => {
  const priceId = PADDLE_PRICE_IDS[packId];
  if (!priceId || !COIN_PACKS.some((p) => p.id === packId) || !webCoinPurchasesEnabled())
    return 'unavailable';
  const userId = account?.userId ?? null;
  if (!userId) return 'signed_out';
  try {
    const P = await loadPaddle();
    pendingPack = packId;
    track('purchase_started', {
      product_id: packId,
      price_tier: COIN_PACKS.find((p) => p.id === packId)!.usd.toFixed(2),
      store: 'paddle',
    });
    P.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customData: { user_id: userId },
      settings: { displayMode: 'overlay', theme: 'dark', allowLogout: false },
    });
    return 'opened';
  } catch {
    return 'unavailable';
  }
};
